/**
 * WAM → EffectPlugin adapter.
 *
 * Wraps a loaded WAM class in our `EffectPlugin` contract so it slots
 * into the plugin registry alongside Tone-backed effects. The shape
 * match is close to 1:1 — the only gymnastics happen around:
 *
 *   1. **Param writes**: our plugin contract calls `setParam(id, value)`
 *      with a naked number. WAM v2 wants `scheduleEvents(...)` with an
 *      automation event record. The adapter bridges that.
 *   2. **GUI surface**: WAMs expose `instance.createGui()` returning an
 *      `HTMLElement`, not a React component. The Plugin-tab routes
 *      `wam-*` plugin ids to a dedicated `<WamGui>` component that
 *      mounts the element imperatively.
 *   3. **Sync instantiation**: our `createNode` is sync, but
 *      `WamClass.createInstance` is async. We return a handle
 *      immediately with a gain pass-through on both input + output;
 *      once the instance resolves, we re-wire the real `audioNode`
 *      in place. Audio briefly passes through a dry gain wrapper
 *      during the ms-scale instantiation window — inaudible in
 *      practice, and avoids blocking the pad-trigger hot path.
 */

import type {
  EffectPlugin,
  ParamSchema,
  PluginHandle,
} from '@/audio/plugins/types'
import {
  getPreloadedWamClass,
  getWamHostGroupId,
  type WamClass,
  type WamInstance,
  type WamParameterInfoMap,
} from './bootstrap'

/**
 * Descriptor passed to the adapter — identifies the plugin + points
 * at its preloaded URL so the adapter can look up the cached class
 * at `createNode` time.
 */
export interface WamEffectDescriptor {
  /** Registry id — must start with `wam-` so the Plugin-tab resolver
   *  routes to `<WamGui>` instead of a React view. */
  id: string
  label: string
  category: EffectPlugin['category']
  /** Source URL used for `preloadWam` — the adapter fetches the
   *  WamClass from the module-level cache by this key. */
  url: string
  /** Static param schema shown in the generic editor (Cell Editor
   *  compact grid). Pass `[]` if the WAM's params should only be
   *  editable through its native GUI — the schema is informational. */
  paramSchema?: readonly ParamSchema[]
  /** Optional initial default values. Merged with WAM-reported
   *  `defaultValue`s if the WAM exposes `getParameterInfo()`. */
  defaultPreset?: Record<string, number>
}

/**
 * Non-standard extension to `PluginHandle` — exposes the live WAM
 * instance so `<WamGui>` can call `createGui()` + `destroyGui()` on
 * it. The bus layer narrows with `hasWamInstance(handle)` before
 * touching this.
 */
export interface WamPluginHandle extends PluginHandle {
  getWamInstance(): WamInstance | null
}

export function hasWamInstance(h: PluginHandle): h is WamPluginHandle {
  return typeof (h as Partial<WamPluginHandle>).getWamInstance === 'function'
}

/**
 * Translate a WAM param info map into our `ParamSchema[]` shape.
 * Used when the descriptor doesn't provide one explicitly — gives
 * the Cell Editor compact grid something to render even for
 * auto-discovered params.
 */
export function wamParamsToSchema(params: WamParameterInfoMap): ParamSchema[] {
  const out: ParamSchema[] = []
  for (const [id, info] of Object.entries(params)) {
    if (info.type === 'choice' && info.choices) {
      out.push({
        id,
        label: info.label ?? id,
        kind: 'select',
        default: info.defaultValue,
        options: info.choices.map((label, i) => [i, label] as [number, string]),
      })
    } else if (info.type === 'boolean') {
      out.push({
        id,
        label: info.label ?? id,
        kind: 'toggle',
        default: info.defaultValue,
      })
    } else {
      out.push({
        id,
        label: info.label ?? id,
        kind: 'slider',
        min: info.minValue,
        max: info.maxValue,
        step: info.discreteStep,
        default: info.defaultValue,
      })
    }
  }
  return out
}

/**
 * Build an `EffectPlugin` factory from a WAM descriptor. The WAM
 * class MUST already be preloaded (via `preloadWam` during engine
 * boot) — the returned `createNode` is synchronous and would throw
 * otherwise.
 *
 * If the preload failed (class is null), the factory returns a
 * pass-through no-op plugin so the bus graph stays intact + the UI
 * shows a placeholder. This keeps a broken WAM from taking down the
 * engine.
 */
export function createWamEffectPlugin(
  descriptor: WamEffectDescriptor,
): EffectPlugin {
  const defaultPreset = descriptor.defaultPreset ?? {}
  const paramSchema = descriptor.paramSchema ?? []

  return {
    id: descriptor.id,
    label: descriptor.label,
    category: descriptor.category,
    defaultPreset,
    paramSchema: [...paramSchema],
    createNode(ctx, preset = {}): WamPluginHandle {
      return buildWamEffectHandle(ctx, descriptor, {
        ...defaultPreset,
        ...preset,
      })
    },
  }
}

function buildWamEffectHandle(
  ctx: AudioContext,
  descriptor: WamEffectDescriptor,
  preset: Record<string, number>,
): WamPluginHandle {
  // Input + output gain wrappers. Pass-through by default, but also
  // give us a stable `AudioNode` to hand back synchronously while
  // the async `createInstance` resolves — once it does, we re-wire
  // the real `instance.audioNode` between them.
  const input = ctx.createGain()
  const output = ctx.createGain()
  // Temporary direct passthrough until the WAM is wired in.
  input.connect(output)

  let instance: WamInstance | null = null
  let disposed = false
  // Buffered setParam calls made before the instance resolves.
  const pendingParams: Array<{ id: string; value: number }> = []

  const wamClass: WamClass | null = getPreloadedWamClass(descriptor.url)
  if (!wamClass) {
    // Preload failed — return a pass-through handle. setParam is a
    // no-op and getWamInstance returns null so `<WamGui>` can show a
    // placeholder.
    console.warn(
      `[wam] ${descriptor.id}: no preloaded class for ${descriptor.url}; rendering pass-through`,
    )
    return {
      input,
      output,
      setParam() {
        // no-op
      },
      dispose() {
        try {
          input.disconnect()
          output.disconnect()
        } catch {
          /* already disconnected */
        }
      },
      getWamInstance() {
        return null
      },
    }
  }

  const hostGroupId = getWamHostGroupId(ctx)

  // Kick off async instantiation. Once resolved, bypass the temp
  // passthrough + route audio through the WAM's audioNode.
  wamClass
    .createInstance(hostGroupId, ctx)
    .then(created => {
      if (disposed) {
        created.destroy()
        return
      }
      instance = created
      // Swap the passthrough for the real DSP path:
      //   input → instance.audioNode → output
      try {
        input.disconnect()
      } catch {
        /* ignore */
      }
      input.connect(instance.audioNode)
      instance.audioNode.connect(output)
      // Apply any params set during the instantiation window.
      for (const { id, value } of pendingParams) {
        writeWamParam(instance, id, value)
      }
      pendingParams.length = 0
      // Apply preset defaults.
      for (const [id, value] of Object.entries(preset)) {
        writeWamParam(instance, id, value)
      }
    })
    .catch(err => {
      console.warn(`[wam] ${descriptor.id} createInstance failed:`, err)
    })

  return {
    input,
    output,
    setParam(name, value) {
      if (!instance) {
        pendingParams.push({ id: name, value })
        return
      }
      writeWamParam(instance, name, value)
    },
    dispose() {
      disposed = true
      try {
        input.disconnect()
        output.disconnect()
      } catch {
        /* already disconnected */
      }
      if (instance) {
        try {
          instance.destroy()
        } catch (err) {
          console.warn(`[wam] ${descriptor.id} destroy failed:`, err)
        }
      }
    },
    getWamInstance() {
      return instance
    },
  }
}

function writeWamParam(instance: WamInstance, id: string, value: number): void {
  try {
    instance.audioNode.scheduleEvents({
      type: 'wam-automation',
      time: 0,
      data: { id, value, normalized: false },
    })
  } catch (err) {
    console.warn(`[wam] scheduleEvents failed for param ${id}:`, err)
  }
}
